import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, Download, Trash2, Upload, RotateCcw, Loader2,
  ShieldAlert, Clock, HardDrive, CalendarClock, CheckCircle2, RefreshCw,
  GitBranch, XCircle,
} from "lucide-react";

function humanBytes(n) {
  if (!n && n !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function humanDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminBackups() {
  const [rows, setRows] = useState([]);
  const [deploys, setDeploys] = useState([]);
  const [retention, setRetention] = useState(14);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [createNote, setCreateNote] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const [b, d] = await Promise.all([
        api.get("/commissions/backups"),
        api.get("/commissions/deploys").catch(() => ({ data: { deploys: [] } })),
      ]);
      setRows(b.data.backups || []);
      setRetention(b.data.retention_auto || 14);
      setDeploys(d.data.deploys || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load backups");
    } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  async function createSnapshot() {
    setCreating(true);
    try {
      const { data } = await api.post("/commissions/backups",
        { note: createNote || "" }, { timeout: 600000 });
      toast.success(`Snapshot created: ${data.name} (${humanBytes(data.size_bytes)})`);
      setCreateNote("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Snapshot failed");
    } finally { setCreating(false); }
  }

  async function download(name) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/commissions/backups/${encodeURIComponent(name)}/download`;
    // Use fetch so we can pass the Authorization header; the direct-link
    // approach would strip the JWT bearer.
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success(`Downloading ${name}.tar.gz`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  }

  async function del(name) {
    if (!window.confirm(`Delete snapshot ${name}? This removes the tarball from disk and cannot be undone.`)) return;
    try {
      await api.delete(`/commissions/backups/${encodeURIComponent(name)}`);
      toast.success("Snapshot deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  async function upload(evt) {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    if (!file.name.endsWith(".tar.gz") && !file.name.endsWith(".tgz")) {
      toast.error("Upload must be a .tar.gz produced by this app");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", `Uploaded ${file.name}`);
      const { data } = await api.post("/commissions/backups/upload", form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      toast.success(`Uploaded: ${data.name} (${humanBytes(data.size_bytes)})`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  }

  function openRestore(row) {
    setRestoreTarget(row);
    setRestoreConfirm("");
  }

  async function doRestore() {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const { data } = await api.post(
        `/commissions/backups/${encodeURIComponent(restoreTarget.name)}/restore`,
        { confirm: restoreConfirm },
        { timeout: 900000 });
      toast.success(`Restored ${data.name} — reloading…`);
      setRestoreTarget(null);
      // Force reload — auth state, tenant config, everything must refetch
      setTimeout(() => { window.location.href = "/admin"; }, 1200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    } finally { setRestoring(false); }
  }

  const totalSize = rows.reduce((a, r) => a + (r.size_bytes || 0), 0);

  return (
    <div className="space-y-8" data-testid="admin-backups">
      <div>
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-cyan-400" />
          <h1 className="text-3xl font-bold text-slate-100">Backups &amp; restore</h1>
        </div>
        <p className="mt-2 text-sm text-slate-400 max-w-3xl">
          Full MongoDB dumps of this tenant&apos;s database. Manual snapshots are kept forever,
          automatic snapshots keep the most recent <b className="text-slate-300">{retention}</b> and
          rotate the rest. Restore is destructive — every collection is dropped and reloaded.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Snapshots on disk</div>
          <div className="mt-2 text-2xl font-bold text-slate-100" data-testid="backups-count">
            {loading ? "…" : rows.filter(r => r.exists_on_disk).length}
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total size</div>
          <div className="mt-2 text-2xl font-bold text-slate-100">{humanBytes(totalSize)}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Last snapshot</div>
          <div className="mt-2 text-sm font-medium text-slate-100">
            {loading ? "…" : (rows[0] ? humanDate(rows[0].created_at) : "Never")}
          </div>
        </div>
      </div>

      {/* Actions row */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <HardDrive className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Create a snapshot right now</h2>
        </div>
        <p className="text-xs text-slate-400">
          Take a snapshot before pushing any risky change. Tag it with a note so you can find it later.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="backup-note-input"
            type="text"
            value={createNote}
            onChange={(e) => setCreateNote(e.target.value)}
            placeholder="e.g. Before deploying commissions calc v2"
            className="flex-1 rounded-lg bg-slate-950 border border-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            maxLength={400}
          />
          <button
            data-testid="backup-create-btn"
            onClick={createSnapshot}
            disabled={creating}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60 transition-colors"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {creating ? "Snapshotting…" : "Create snapshot"}
          </button>
          <label
            data-testid="backup-upload-label"
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-sm text-slate-300 cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload .tar.gz"}
            <input
              data-testid="backup-upload-input"
              type="file"
              accept=".tar.gz,.tgz,application/gzip"
              className="hidden"
              onChange={upload}
            />
          </label>
        </div>
      </div>

      {/* Snapshot table */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800">
          <Clock className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Snapshot history</h2>
          <button
            data-testid="backup-refresh-btn"
            onClick={reload}
            className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            No snapshots yet. Click <b className="text-slate-300">Create snapshot</b> above
            or wait for tonight&apos;s automatic run.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[11px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Type</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Created</th>
                  <th className="text-left px-3 py-2.5 font-semibold">By</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Size</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Note</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.name} data-testid={`backup-row-${r.name}`}
                       className={r.exists_on_disk ? "" : "opacity-50"}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-200">{r.name}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${
                        r.kind === "manual" ? "bg-cyan-500/15 text-cyan-300" :
                        r.kind === "upload" ? "bg-purple-500/15 text-purple-300" :
                        "bg-slate-800 text-slate-400"
                      }`}>{r.kind}</span>
                    </td>
                    <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{humanDate(r.created_at)}</td>
                    <td className="px-3 py-3 text-slate-400">{r.created_by}</td>
                    <td className="px-3 py-3 text-right text-slate-300 whitespace-nowrap">{humanBytes(r.size_bytes)}</td>
                    <td className="px-3 py-3 text-slate-400 max-w-xs truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`backup-download-${r.name}`}
                          onClick={() => download(r.name)}
                          disabled={!r.exists_on_disk}
                          className="p-2 rounded-md hover:bg-slate-800 text-slate-300 hover:text-cyan-300 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Download tarball"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`backup-restore-${r.name}`}
                          onClick={() => openRestore(r)}
                          disabled={!r.exists_on_disk}
                          className="p-2 rounded-md hover:bg-amber-500/20 text-amber-300 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Restore this snapshot (DESTRUCTIVE)"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`backup-delete-${r.name}`}
                          onClick={() => del(r.name)}
                          className="p-2 rounded-md hover:bg-red-500/20 text-red-300"
                          title="Delete snapshot"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restore confirmation modal */}
      {restoreTarget && (
        <div
          data-testid="backup-restore-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !restoring && setRestoreTarget(null)}
        >
          <div
            className="w-full max-w-lg rounded-xl border border-amber-500/40 bg-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-400" />
              <h3 className="text-base font-semibold text-slate-100">Restore snapshot — destructive</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                <p className="font-semibold">This will drop every collection in the database and replace it with the snapshot&apos;s contents.</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Every write since <b className="font-mono">{humanDate(restoreTarget.created_at)}</b> will be lost.</li>
                  <li>All logged-in users will be signed out on their next request.</li>
                  <li>Any snapshots newer than this one will still be listed but reflect the older state.</li>
                </ul>
              </div>
              <div className="text-xs text-slate-400">
                <div><span className="text-slate-500">Snapshot:</span> <span className="font-mono text-slate-200">{restoreTarget.name}</span></div>
                <div><span className="text-slate-500">Size:</span> {humanBytes(restoreTarget.size_bytes)} · <span className="text-slate-500">Collections:</span> {restoreTarget.collections || "—"}</div>
                {restoreTarget.note && (
                  <div className="mt-1"><span className="text-slate-500">Note:</span> {restoreTarget.note}</div>
                )}
              </div>
              <label className="block text-xs text-slate-300">
                Type the snapshot name to confirm:
                <input
                  data-testid="backup-restore-confirm-input"
                  autoFocus
                  value={restoreConfirm}
                  onChange={(e) => setRestoreConfirm(e.target.value)}
                  className="mt-1 w-full rounded-md bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  placeholder={restoreTarget.name}
                />
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button
                  data-testid="backup-restore-cancel"
                  disabled={restoring}
                  onClick={() => setRestoreTarget(null)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  data-testid="backup-restore-confirm"
                  onClick={doRestore}
                  disabled={restoring || restoreConfirm !== restoreTarget.name}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40"
                >
                  {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  {restoring ? "Restoring…" : "Restore now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Docs footer */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 text-xs text-slate-400 space-y-2">
        <div className="flex items-center gap-2 text-slate-300 font-semibold text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          Recommended flow before shipping a risky change
        </div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Click <b className="text-slate-200">Create snapshot</b> and tag it with what you&apos;re about to change.</li>
          <li>Deploy your change and confirm it behaves.</li>
          <li>If anything went wrong, use the <b className="text-slate-200">Restore</b> button on that snapshot to roll back.</li>
        </ol>
        <div className="flex items-center gap-2 pt-2 text-slate-500">
          <CalendarClock className="w-3.5 h-3.5" />
          Automatic snapshots run every night after 17:00 UTC (03:00–04:00 Sydney). A pre-migration snapshot is also taken automatically whenever a new schema migration is pending at startup.
        </div>
      </div>

      {/* Recent deploys / boot log */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden" data-testid="deploy-log">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800">
          <GitBranch className="w-4 h-4 text-cyan-400" />
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Recent deploys</h2>
          <span className="ml-auto text-[10px] font-mono text-slate-500">last {deploys.length}</span>
        </div>
        {deploys.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">
            No boots recorded yet. The next backend restart will log one here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[11px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Version</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Booted</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Migrations applied</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Pre-migration snapshot</th>
                  <th className="text-center px-5 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {deploys.map((d, idx) => (
                  <tr key={d.started_at || `deploy-${idx}`} data-testid={`deploy-row-${idx}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-200">v{d.version || "unknown"}</td>
                    <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{humanDate(d.started_at)}</td>
                    <td className="px-3 py-3 text-slate-400">
                      {(d.migrations_applied && d.migrations_applied.length)
                        ? <span className="font-mono text-xs text-cyan-300">{d.migrations_applied.join(", ")}</span>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      {d.pre_migration_snapshot
                        ? <span className="font-mono text-[11px] text-amber-300">{d.pre_migration_snapshot}</span>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {d.boot_ok
                        ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3.5 h-3.5" />OK</span>
                        : <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3.5 h-3.5" />Failed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
